import { useState } from 'react';
import { Check, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useMilestoneSets } from '@/hooks/useMilestoneSets';

interface MilestoneSetSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (milestoneSet: any) => void;
}

export function MilestoneSetSelector({ open, onOpenChange, onSelect }: MilestoneSetSelectorProps) {
  const [search, setSearch] = useState('');
  const [previewSet, setPreviewSet] = useState<any>(null);

  const { milestoneSets, loading } = useMilestoneSets({
    search: search || undefined,
  });

  const handleSelect = (set: any) => {
    onSelect(set);
    onOpenChange(false);
  };

  const categories = [
    { value: 'design', label: 'Design' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'installation', label: 'Installation' },
    { value: 'construction', label: 'Construction' },
    { value: 'software', label: 'Software Development' },
    { value: 'consulting', label: 'Consulting' },
  ];

  return (
    <>
      <Dialog open={open && !previewSet} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Milestone Set</DialogTitle>
            <DialogDescription>
              Choose a saved milestone set to apply to this job
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              placeholder="Search milestone sets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardHeader>
                      <Skeleton className="h-6 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardHeader>
                  </Card>
                ))}
              </div>
            ) : milestoneSets.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <p className="text-muted-foreground">
                    {search ? 'No milestone sets match your search' : 'No milestone sets available'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {milestoneSets.map((set) => (
                  <Card key={set.id} className="hover:shadow-md transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{set.name}</CardTitle>
                          {set.category && (
                            <Badge variant="outline" className="mt-1">
                              {categories.find(c => c.value === set.category)?.label || set.category}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <CardDescription>
                        {set.description || 'No description'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium">{set.milestone_template_items?.length || 0}</span> milestones
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPreviewSet(set)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Preview
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleSelect(set)}
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Select
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      {previewSet && (
        <Dialog open={!!previewSet} onOpenChange={() => setPreviewSet(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{previewSet.name}</DialogTitle>
              <DialogDescription>
                {previewSet.milestone_template_items?.length || 0} milestones
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              {previewSet.milestone_template_items?.map((milestone: any, index: number) => (
                <Card key={index}>
                  <CardContent className="pt-4">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium">{milestone.milestone_name}</div>
                          {milestone.description && (
                            <div className="text-sm text-muted-foreground mt-1">
                              {milestone.description}
                            </div>
                          )}
                        </div>
                        <Badge variant="outline" className="ml-2">
                          {index + 1}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Type:</span>{' '}
                          <span className="font-medium capitalize">
                            {milestone.milestone_type.replace('_', ' ')}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Owner:</span>{' '}
                          <span className="font-medium capitalize">{milestone.action_owner}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Deliverable:</span>{' '}
                          <span className="font-medium">{milestone.deliverable}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setPreviewSet(null)}>
                Back
              </Button>
              <Button onClick={() => {
                handleSelect(previewSet);
                setPreviewSet(null);
              }}>
                <Check className="h-4 w-4 mr-1" />
                Select This Set
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
